import api from './api';
import type { CalcScratchBundle } from '../components/easyEstimate/PozycjaCalcScratchpad';

/**
 * Biblioteka pozycji zweryfikowanych (źródło ATH).
 * Backend: `/api/easy-estimate/verified-library` (src/controllers/easyEstimateVerifiedLibraryController.js).
 * Jedyne miejsce dostępu z frontu — strona `/app/easy-estimate/zweryfikowane`.
 */

export interface VerifiedLibRmsRow {
  typ: string;
  nazwa: string;
  jm?: string;
  norma: number;
  cenaJedn: number;
  wartosc: number;
}

/** Stan rozszerzony zapisywany w kolumnie extras_json. */
export interface VerifiedLibExtras {
  /** Snapshot mini-arkusza scratchpada (Własne/KNR/Gemma) — rekonstruowany po otwarciu panelu. */
  calcScratchBundle?: CalcScratchBundle;
  /** Surowe wiersze RMS (po rozwinięciu narzutów) — do odświeżenia karty KNR przy otwarciu. */
  rmsRows?: VerifiedLibRmsRow[];
  /** Dowolne nadpisania komórek, jeśli pojawią się w przyszłości. */
  cellOverrides?: Record<string, string>;
  /** Pochodzenie pozycji (docelowo może być więcej źródeł niż ATH). */
  source?: { kind: 'ath'; filename?: string | null };
}

/** Dozwolone branże (zgodne z kontrolerem backendu). */
export const ALLOWED_BRANZE = [
  'budowlana',
  'elektryczna',
  'sanitarna',
  'wentylacja',
  'drogowa',
  'inne',
] as const;
export type LibraryBranza = (typeof ALLOWED_BRANZE)[number];

/** Etykiety dla UI (pierwsza litera wielka). */
export const BRANZA_LABELS: Record<LibraryBranza, string> = {
  budowlana: 'Budowlana',
  elektryczna: 'Elektryczna',
  sanitarna: 'Sanitarna',
  wentylacja: 'Wentylacja',
  drogowa: 'Drogowa',
  inne: 'Inne',
};

export interface VerifiedLibPositionDto {
  id: number;
  podstawa: string;
  opis: string | null;
  jm: string | null;
  ilosc: string | null;
  /** Cena jedn. z sumy RMS × norma (z ATH). */
  cena_jedn: number;
  uwagi: string | null;
  branza: LibraryBranza | null;
  source_ath_filename: string | null;
  source_ath_batch_id: string | null;
  source_ath_ordinal: number | null;
  extras: VerifiedLibExtras | null;
  created_at: string;
  updated_at: string;
}

export const listVerifiedLibrary = async (): Promise<VerifiedLibPositionDto[]> => {
  const res = await api.get<{ success: boolean; rows: VerifiedLibPositionDto[] }>(
    '/easy-estimate/verified-library'
  );
  return Array.isArray(res.data?.rows) ? res.data.rows : [];
};

export const importVerifiedLibraryAth = async (
  file: File
): Promise<{ imported_positions: number; batch_id: string; filename: string | null }> => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/easy-estimate/verified-library/import-ath', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const updateVerifiedLibraryPosition = async (
  id: number,
  payload: Partial<{
    podstawa: string;
    opis: string | null;
    jm: string | null;
    ilosc: string | null;
    cena_jedn: number;
    uwagi: string | null;
    branza: LibraryBranza | null;
    extras: VerifiedLibExtras | null;
  }>
): Promise<void> => {
  await api.patch(`/easy-estimate/verified-library/${id}`, payload);
};

export const deleteVerifiedLibraryPosition = async (id: number): Promise<void> => {
  await api.delete(`/easy-estimate/verified-library/${id}`);
};

/* =================== Akcje zbiorcze =================== */

export const bulkDeleteVerifiedLibrary = async (
  ids: number[]
): Promise<{ deleted: number }> => {
  const res = await api.post('/easy-estimate/verified-library/bulk-delete', { ids });
  return { deleted: Number(res.data?.deleted) || 0 };
};

/** Zmiana cena jedn. o procent (np. percent=10 → +10%, percent=-5 → -5%). */
export const bulkPricePercentVerifiedLibrary = async (
  ids: number[],
  percent: number
): Promise<{ updated: number }> => {
  const res = await api.post('/easy-estimate/verified-library/bulk-price-percent', {
    ids,
    percent,
  });
  return { updated: Number(res.data?.updated) || 0 };
};

/** Ustawia stawkę roboczogodziny (zł/rg) dla wierszy R w KNR i przelicza cena_jedn. */
export const bulkSetRgVerifiedLibrary = async (
  ids: number[],
  rate: number
): Promise<{ updated: number }> => {
  const res = await api.post('/easy-estimate/verified-library/bulk-set-rg', {
    ids,
    rate,
  });
  return { updated: Number(res.data?.updated) || 0 };
};

export const bulkSetBranzaVerifiedLibrary = async (
  ids: number[],
  branza: LibraryBranza | null
): Promise<{ updated: number }> => {
  const res = await api.post('/easy-estimate/verified-library/bulk-set-branza', {
    ids,
    branza,
  });
  return { updated: Number(res.data?.updated) || 0 };
};
